import { useEffect, useState } from "react";
import { apiFetch, send, TENANT } from "../api.js";

/** Selector names are the model's words. These are the reader's. */
const SELECTOR_LABELS: Record<string, string> = {
  phase: "one step",
  project: "a project",
  resource: "a person",
  discipline: "a discipline",
  work_type: "a work type",
  priority: "a priority",
  customer: "a customer",
  all: "everything",
};
const selectorLabel = (s: string) => SELECTOR_LABELS[s] ?? s;

interface Effect {
  changes: string;
  defaultFrom: string;
  param?: string;
}

interface Term {
  term_id: string; name: string; kind: "hard" | "soft"; compiler_key: string;
  enabled: boolean; label: string | null; description: string | null;
  default_weight: number | null; normalizer: string | null;
  allowed_selectors: string[] | null; planner_examples: string[] | null;
  param_bounds: Record<string, unknown> | null;
  disabled_reason: string | null;
  /** Joined on by the server from the compiler itself, so it cannot drift. */
  effect: Effect | null;
}

/**
 * The bounds a term's parameter must stay inside.
 *
 * Stored as JSON because the shape differs per term: a numeric range for a
 * percentage, a list for an enumerated bound. It was in the database and on no
 * screen, which is how `allow_overtime` came to be a row with a checkbox and
 * nothing to say what number it moved or how far.
 */
function ParamBounds({ term, onSave, readOnly }: {
  term: Term;
  onSave: (next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const bounds = term.param_bounds ?? {};
  const keys = Object.keys(bounds);

  if (!keys.length) {
    return (
      <div className="fld">
        <span>limits</span>
        <div className="ro muted">
          {term.effect?.param
            ? `carries ${term.effect.param}, unbounded`
            : "this term carries no parameter"}
        </div>
      </div>
    );
  }

  return (
    <div className="fld">
      <span>limits · the range the assistant is allowed to ask for</span>
      {keys.map((k) => {
        const v = bounds[k] as any;
        const isRange = v && typeof v === "object" && !Array.isArray(v);
        return (
          <div key={k} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <span className="mono" style={{ minWidth: 60 }}>{k}</span>
            {isRange ? (
              <>
                <label style={{ fontSize: 12 }}>min
                  <input className="num" type="number" defaultValue={v.min} disabled={readOnly}
                    onBlur={(e) => onSave({ ...bounds, [k]: { ...v, min: Number(e.target.value) } })} />
                </label>
                <label style={{ fontSize: 12 }}>max
                  <input className="num" type="number" defaultValue={v.max} disabled={readOnly}
                    onBlur={(e) => onSave({ ...bounds, [k]: { ...v, max: Number(e.target.value) } })} />
                </label>
              </>
            ) : Array.isArray(v) ? (
              <input style={{ flex: 1 }} defaultValue={v.join(", ")} disabled={readOnly}
                onBlur={(e) => onSave({
                  ...bounds,
                  [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                })} />
            ) : (
              <span className="mono muted">{String(v)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The DSL editor. Everything here is data; the compiler behind each term is not,
 * which is why compiler_key is shown but never editable.
 */
/**
 * What this term does to the real backlog, in numbers.
 *
 * The screen above says what a term is FOR, in a sentence engineering wrote.
 * That sentence was confidently wrong about three terms for months, and no
 * amount of reading it would have revealed that. This runs the term against
 * the live schedule and reports the difference, which cannot be wrong in the
 * same way.
 */
function EffectPanel({ termName }: { termName: string }) {
  const [data, setData] = useState<any>(null);
  const [busy, setBusy] = useState<"" | "compile" | "solve">("compile");
  const [err, setErr] = useState<string | null>(null);

  async function load(solve: boolean) {
    setBusy(solve ? "solve" : "compile");
    setErr(null);
    try {
      const r = await apiFetch(`/v1/sentry/dsl/effect`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: TENANT, probe: termName, solve }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? `failed (${r.status})`);
      setData(j);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  useEffect(() => {
    setData(null);
    void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termName]);

  const s = data?.solve;
  const fmt = (n: number | null | undefined) => (n === null || n === undefined ? "—" : n.toLocaleString());

  return (
    <div className="term-effect" style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <b>Against your schedule right now</b>
        {busy === "compile" && <span className="muted" style={{ fontSize: 12 }}>checking…</span>}
      </div>

      {err && <div className="err" style={{ marginTop: 6 }}>{err}</div>}

      {data?.probeTerm && (
        <div className="muted mono" style={{ fontSize: 11, marginTop: 6 }}>
          tried: {JSON.stringify(data.probeTerm)}
        </div>
      )}

      {/*
        Two very different statements, and they were being collapsed into one.
        "I could not build a fair example" is not an accusation; "this rule is
        inert" is. Saying the second when you mean the first teaches the reader
        to ignore the warning that matters.
      */}
      {data?.probeIncomplete && (
        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          No automatic example for this one — {data.probeIncomplete}. The rule itself is fine;
          try it through the assistant with a real request instead.
        </div>
      )}

      {data && data.inert && !data.probeIncomplete && (
        <div className="err" style={{ marginTop: 8 }}>
          <b>This rule changes nothing.</b> It compiled without error and altered no part of the
          plan — so switching it on would let the assistant accept requests it then quietly drops.
        </div>
      )}

      {data && !data.inert && !!(data.summary as string[])?.length && (
        <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
          {(data.summary as string[]).map((line) => <li key={line}>{line}</li>)}
        </ul>
      )}

      {data && (
        <div style={{ marginTop: 10 }}>
          <button className="btn" disabled={busy !== ""} onClick={() => void load(true)}>
            {busy === "solve" ? "solving both…" : "Solve with and without →"}
          </button>
          {!s && (
            <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>
              runs the optimizer twice; takes a few seconds
            </span>
          )}
        </div>
      )}

      {s && (
        <table className="tbl" style={{ marginTop: 10, fontSize: 12 }}>
          <thead>
            <tr><th /><th>without</th><th>with</th></tr>
          </thead>
          <tbody>
            <tr><td>status</td><td>{s.before.status}</td><td>{s.after.status}</td></tr>
            <tr><td>objective</td><td>{fmt(s.before.objectiveValue)}</td><td>{fmt(s.after.objectiveValue)}</td></tr>
            {/* A wide gap usually means the solver could not PROVE a tight
                lower bound, not that the plan is bad. Said here so nobody
                reads it as a quality score. */}
            <tr>
              <td>proved to within</td>
              <td>{s.before.gapPct === null ? "—" : `${s.before.gapPct}%`}</td>
              <td>{s.after.gapPct === null ? "—" : `${s.after.gapPct}%`}</td>
            </tr>
            <tr><td>last finish (block)</td><td>{fmt(s.before.lastFinishBlock)}</td><td>{fmt(s.after.lastFinishBlock)}</td></tr>
            <tr><td>people changed</td><td colSpan={2}>{s.reassignments} phase(s) move</td></tr>
            {!!s.after.infeasibleCore?.length && (
              <tr><td>refused</td><td colSpan={2} className="mono">{s.after.infeasibleCore.join(" · ")}</td></tr>
            )}
            {!!s.violations?.length && (
              <tr><td>re-check</td><td colSpan={2} className="err">{s.violations.length} violation(s)</td></tr>
            )}
          </tbody>
        </table>
      )}
      {s && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          “proved to within” is how far the answer might sit from the best possible one. A wide
          figure usually means the solver could not prove a tight bound in the time it had, not
          that the plan is poor.
        </div>
      )}
    </div>
  );
}

export function DslAdmin({ reload, go }: { reload: () => void; go?: (view: string) => void }) {
  const [terms, setTerms] = useState<Term[]>([]);
  const [usable, setUsable] = useState<Set<string>>(new Set());
  const [rejected, setRejected] = useState<string[]>([]);
  const [sel, setSel] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [test, setTest] = useState<{ intent: string; out: any } | null>(null);

  async function load() {
    const [all, vocab] = await Promise.all([
      apiFetch(`/v1/sentry/dsl/terms?tenantId=${TENANT}`).then((r) => r.json()),
      apiFetch(`/v1/sentry/dsl/vocabulary?tenantId=${TENANT}`).then((r) => r.json()),
    ]);
    setTerms(all.terms ?? []);
    setUsable(new Set((vocab.vocab ?? []).map((t: any) => t.name)));
    setRejected(vocab.rejected ?? []);
  }
  useEffect(() => { void load(); }, []);

  async function patch(t: Term, body: unknown, force = false) {
    setErr(null);
    try {
      await send(`/dsl/terms/${t.term_id}`, "PATCH", force ? { ...(body as object), force: true } : body);
      await load(); reload();
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes("referenced by") && confirm(`${msg}. Disable anyway?`)) return patch(t, body, true);
      setErr(msg);
    }
  }

  async function runTest(intent: string) {
    const r = await apiFetch(`/v1/sentry/ask`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ tenantId: TENANT, intent, dryRun: true }),
    });
    setTest({ intent, out: await r.json() });
  }

  const current = terms.find((t) => t.term_id === sel) ?? null;

  return (
    <div>
      <p className="hint" style={{ marginTop: 0 }}>
        These are the rules the assistant is allowed to use when you ask for something. It
        cannot invent one. A rule is only usable if the optimizer actually implements it —
        one that does not would be accepted and then quietly ignored, so it is refused
        instead and shown as <b>blocked</b>.
        {go && (
          <>
            {" "}
            <button className="btn" style={{ marginLeft: 6 }} onClick={() => go("rules")}>
              How the rules work →
            </button>
          </>
        )}
      </p>
      {!!rejected.length && (
        <div className="err" style={{ marginBottom: 12 }}>
          <b>Enabled but not usable:</b>
          {rejected.map((r) => <div className="mono" key={r} style={{ fontSize: 12 }}>{r}</div>)}
        </div>
      )}
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}

      <div className="dsl">
        <div className="dsl-list">
          {(["hard", "soft"] as const).map((kind) => (
            <div key={kind}>
              <div className="dsl-grp">{kind} · {terms.filter((t) => t.kind === kind && t.enabled).length} enabled</div>
              {terms.filter((t) => t.kind === kind).map((t) => (
                <div key={t.term_id} className={`dsl-i${sel === t.term_id ? " on" : ""}${t.enabled ? "" : " off"}`}
                  onClick={() => setSel(t.term_id)}>
                  {t.name}
                  <span className="t">{!t.enabled ? "off" : usable.has(t.name) ? "on" : "BLOCKED"}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="dsl-ed">
          {!current ? (
            <p className="muted">Pick a term.</p>
          ) : (
            <>
              <h3 style={{ margin: "0 0 4px" }}>{current.name}</h3>

              {/* What it does, before anything about how it is configured. */}
              {current.effect ? (
                <div className="term-effect">
                  <div>{current.effect.changes}</div>
                  <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                    <b>Without it:</b> {current.effect.defaultFrom}
                    {current.effect.param && (
                      <> · <b>carries</b> <span className="mono">{current.effect.param}</span></>
                    )}
                  </div>
                </div>
              ) : (
                <div className="err" style={{ marginBottom: 10 }}>
                  No compiler in this build implements <span className="mono">{current.compiler_key}</span>,
                  so nothing this term says would take effect.
                </div>
              )}

              <EffectPanel termName={current.name} />

              {!current.enabled && current.disabled_reason && (
                <div className="fld"><span>why it is off</span>
                  <div className="ro">{current.disabled_reason}</div>
                </div>
              )}

              <div className="fld2">
                <div className="fld"><span>kind</span>
                  <div>
                    {current.kind === "hard"
                      ? <><b>Must</b> <span className="muted">— the plan may not break it</span></>
                      : <><b>Prefer</b> <span className="muted">— traded off against the other preferences</span></>}
                  </div>
                </div>
                <div className="fld"><span>status</span>
                  <div>
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input type="checkbox" checked={current.enabled}
                        onChange={(e) => void patch(current, { enabled: e.target.checked })} />
                      {current.enabled ? (usable.has(current.name) ? "enabled and usable" : "enabled but BLOCKED by the solver") : "disabled"}
                    </label>
                  </div>
                </div>
              </div>

              <ParamBounds term={current} readOnly={false}
                onSave={(next) => void patch(current, { param_bounds: next })} />
              <div className="fld"><span>name shown to you</span>
                <input defaultValue={current.label ?? ""} onBlur={(e) => e.target.value !== current.label && void patch(current, { label: e.target.value })} />
              </div>
              <div className="fld"><span>description · the assistant quotes this back when it uses the rule</span>
                <textarea rows={3} defaultValue={current.description ?? ""}
                  onBlur={(e) => e.target.value !== current.description && void patch(current, { description: e.target.value })} />
              </div>
              <div className="fld"><span>phrasings · one per line. How somebody might ask for this, so the assistant recognizes the request</span>
                <textarea rows={4} defaultValue={(current.planner_examples ?? []).join("\n")}
                  onBlur={(e) => void patch(current, { planner_examples: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} />
              </div>
              <div className="fld2">
                {current.kind === "soft" && (
                  <div className="fld"><span>strength · how hard this preference argues against the others</span>
                    <input type="number" step="0.1" defaultValue={current.default_weight ?? 0}
                      onBlur={(e) => void patch(current, { default_weight: Number(e.target.value) })} />
                  </div>
                )}
                <div className="fld"><span>what it can be applied to</span>
                  <div>{(current.allowed_selectors ?? []).map(selectorLabel).join(" · ") || "—"}</div>
                </div>
              </div>

              <details className="fld" style={{ marginTop: 4 }}>
                <summary className="muted" style={{ cursor: "pointer", fontSize: 12 }}>
                  Technical detail
                </summary>
                <div className="fld2" style={{ marginTop: 8 }}>
                  <div className="fld"><span>built-in name · set by engineering, not editable</span>
                    <div className="ro mono">
                      {current.compiler_key} {usable.has(current.name) ? "✓ implemented" : "✗ not implemented"}
                    </div>
                  </div>
                  {current.kind === "soft" && (
                    <div className="fld"><span>normalizer · what its score is divided by, so weights compare</span>
                      <div className="ro mono" style={{ fontSize: 12 }}>{current.normalizer ?? "—"}</div>
                    </div>
                  )}
                </div>
              </details>

              <div className="fld" style={{ marginTop: 10 }}>
                <span>try it · type how you would ask, and see whether the assistant reaches for this rule</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={{ flex: 1 }} placeholder={(current.planner_examples ?? [])[0] ?? "e.g. don't put Eric over 100%"}
                    onKeyDown={(e) => { if (e.key === "Enter") void runTest((e.target as HTMLInputElement).value); }} />
                </div>
              </div>
              {test && (
                <div className="testout">
                  <div className="mono muted">“{test.intent}”</div>
                  {[...(test.out.spec?.hard ?? []), ...(test.out.spec?.soft ?? [])]
                    .filter((t: any) => !String(t.id).startsWith("house "))
                    .map((t: any, i: number) => (
                      <div className="mono" key={i} style={{ color: t.term === current.name ? "var(--good)" : "var(--ink-soft)" }}>
                        {t.term} {JSON.stringify(t.select)}{t.term === current.name ? "  ← this term" : ""}
                      </div>
                    ))}
                  {!!test.out.unmapped?.length && (
                    <div className="mono" style={{ color: "var(--amber)" }}>unmapped: {test.out.unmapped.join(" | ")}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
